<main class="register-shell">
  <section class="register-layout">
    <div class="register-intro">
      <div class="brand-mark">
        <span>{{ 'common.brand' | translate }}</span>
      </div>
      <p>{{ 'register.ssoLabel' | translate }}</p>
      <h1>{{ 'register.heroTitle' | translate }}</h1>
      <span>{{ 'register.heroSubtitle' | translate }}</span>
    </div>

    <nz-card class="register-card" [nzTitle]="'register.cardTitle' | translate">
      <div class="mb-6">
        <nz-steps [nzCurrent]="currentStep" nzSize="small">
          <nz-step [nzTitle]="'register.stepSign' | translate"></nz-step>
          <nz-step [nzTitle]="'register.stepData' | translate"></nz-step>
        </nz-steps>
      </div>

      @if (errorMessage) {
        <nz-alert
          class="mb-4"
          nzType="error"
          [nzMessage]="errorMessage"
          nzShowIcon
        ></nz-alert>
      }

      @if (currentStep === 0) {
        <div class="step-block">
          @if (isLoadingConsent()) {
            <div class="flex justify-center py-6">
              <nz-spin nzSimple></nz-spin>
            </div>
          } @else if (consentDoc()) {
            <app-consent-view/>
          }

          <div class="actions">
            <a nz-button routerLink="/login">{{ 'register.backToLogin' | translate }}</a>
            <button
              nz-button
              nzType="primary"
              type="button"
              [disabled]="!consentDoc()"
              [nzLoading]="isSigning || isVerifyingXml"
              (click)="signXml()"
            >
              {{ 'register.signButton' | translate }}
            </button>
          </div>
        </div>
      }

      @if (currentStep === 1) {
        <form nz-form nzLayout="vertical" class="step-grid" [formGroup]="form">
          <nz-form-item>
            <nz-form-label nzRequired>{{ 'common.iin' | translate }}</nz-form-label>
            <nz-form-control [nzErrorTip]="'register.iinError' | translate">
              <input nz-input formControlName="iin" maxlength="12" autocomplete="username" [placeholder]="'register.iinPlaceholder' | translate">
            </nz-form-control>
          </nz-form-item>

          <nz-form-item>
            <nz-form-label nzRequired>{{ 'register.emailLabel' | translate }}</nz-form-label>
            <nz-form-control [nzErrorTip]="'register.emailError' | translate">
              <div class="flex gap-2">
                <input nz-input type="email" formControlName="email" autocomplete="email" [placeholder]="'register.emailPlaceholder' | translate">
                @if (emailConfirmationRequired()) {
                  @if (emailVerified || codeSent) {
                    <button nz-button type="button" (click)="changeEmail()">{{ 'register.change' | translate }}</button>
                  } @else {
                    <button
                      nz-button
                      type="button"
                      [nzLoading]="isSendingCode"
                      [disabled]="form.controls.email.invalid"
                      (click)="sendEmailCode()"
                    >
                      {{ 'register.sendCode' | translate }}
                    </button>
                  }
                }
              </div>
              @if (emailVerified) {
                <span class="mt-1 flex items-center gap-1 text-sm font-medium text-teal-700">
                  <nz-icon nzType="check-circle" nzTheme="fill"></nz-icon>
                  {{ 'register.emailConfirmed' | translate }}
                </span>
              }
            </nz-form-control>
          </nz-form-item>

          @if (!emailVerified && codeSent) {
            <nz-form-item>
              <nz-form-label nzRequired>{{ 'register.codeLabel' | translate }}</nz-form-label>
              <nz-form-control [nzErrorTip]="'register.codeError' | translate">
                <div class="flex gap-2">
                  <input nz-input formControlName="emailCode" maxlength="6" inputmode="numeric" autocomplete="one-time-code" [placeholder]="'register.codePlaceholder' | translate">
                  <button
                    nz-button
                    type="button"
                    [nzLoading]="isConfirmingCode"
                    [disabled]="form.controls.emailCode.invalid"
                    (click)="confirmCode(form.controls.emailCode.value)"
                  >
                    {{ 'register.confirm' | translate }}
                  </button>
                </div>
                <button
                  nz-button
                  nzType="link"
                  type="button"
                  class="mt-1 px-0"
                  [nzLoading]="isSendingCode"
                  [disabled]="resendCountdown > 0"
                  (click)="sendEmailCode()"
                >
                  {{ resendCountdown > 0
                    ? ('register.resendCountdown' | translate:{seconds: resendCountdown})
                    : ('register.resendCode' | translate) }}
                </button>
              </nz-form-control>
            </nz-form-item>
          }

          <nz-form-item>
            <nz-form-label nzRequired>{{ 'common.psd' | translate }}</nz-form-label>
            <nz-form-control [nzErrorTip]="psdError">
              <nz-input-password [(nzVisible)]="passwordVisible">
                <input nz-input formControlName="password" [placeholder]="'register.psdPlaceholder' | translate">
              </nz-input-password>
            </nz-form-control>
            <ng-template #psdError>
              {{ 'changePsd.rules.prefix' | translate }}@for (requirement of psdRequirements(); track requirement.key; let last = $last) {{{ requirement.key | translate: requirement.params }}{{ last ? '' : ', ' }}}
            </ng-template>
          </nz-form-item>

          <nz-form-item>
            <nz-form-label nzRequired>{{ 'register.confirmPsdLabel' | translate }}</nz-form-label>
            <nz-form-control
              [nzValidateStatus]="form.hasError('psdMismatch') && form.controls.confirmPassword.dirty ? 'error' : form.controls.confirmPassword"
              [nzErrorTip]="form.hasError('psdMismatch') ? ('register.psdMismatch' | translate) : ('register.confirmPsdPlaceholder' | translate)">
              <nz-input-password [(nzVisible)]="confirmPasswordVisible">
                <input nz-input formControlName="confirmPassword" [placeholder]="'register.confirmPsdPlaceholder' | translate">
              </nz-input-password>
            </nz-form-control>
          </nz-form-item>

          <div class="actions">
            <button nz-button type="button" (click)="backToSignStep()">{{ 'common.back' | translate }}</button>
            <button
              nz-button
              nzType="primary"
              type="button"
              [nzLoading]="isSubmitting"
              (click)="submit()"
            >
              {{ 'register.submit' | translate }}
            </button>
          </div>
        </form>
      }
    </nz-card>
  </section>
</main>
